import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DatePicker from 'cccisd-datepicker';

import AggregateAverages from './components/AggregateAverages';
import { noYouthWarning, switchPlanNameIfNeeded } from '../../Administer/helpers';

import {
    setSelectedYouth,
    setStartDateFilter,
    setEndDateFilter,
    setPlanNameFilter,
} from '../../../reducers/dashboard/dashboardSlice';
import { downloadCSV } from '../helpers';
import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from '../../Administer/ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

import style from './style.css';
import ResponseRateBox from './components/ResponseRateBox';
import AlertsResponseRateBox from './components/AlertsResponseRateBox';

const AggregateDataTab = () => {
    // state
    const dispatch = useDispatch();
    const allAnswerData = useSelector(state => {
        return state.app.dashboard.allAnswerData;
    });
    const selectedYouth = useSelector(state => {
        return state.app.dashboard.selectedYouth;
    });
    const youthList = useSelector(state => {
        return state.app.careCoordinator.youthList;
    });
    const aggregateScoreAverages = useSelector(state => {
        return state.app.dashboard.aggregateScoreAverages;
    });

    // filters
    const startDateFilterValue = useSelector(state => state.app.dashboard.filters.startDate);
    const endDateFilterValue = useSelector(state => state.app.dashboard.filters.endDate);
    const planNameFilterValue = useSelector(state => state.app.dashboard.filters.planName);
    const filters = {
        startDate: startDateFilterValue,
        endDate: endDateFilterValue,
        planName: planNameFilterValue,
    };

    if (youthList.length === 0) {
        return noYouthWarning({
            message: (
                <div>
                    Welcome to SMART-Wrap! To get started, please navigate to the
                    <a href="/manage" style={{ color: '#4a2e84' }}>
                        {' '}
                        <u>Manage</u>{' '}
                    </a>
                    tab above to add your families.
                </div>
            ),
            showIcon: false,
        });
    }

    return (
        <div>
            <div className={style.main_container}>
                <div className={style.selector_and_info_container}>
                    <div className={style.selector_controls}>
                        <label className="form-label">Youth: </label>
                        <select
                            className="form-select"
                            onChange={e => {
                                const newValue = e.target.value;
                                const valueIsAll = newValue === 'all';

                                if (valueIsAll) {
                                    dispatch(setSelectedYouth(null));
                                    return;
                                }

                                const selected = youthList.find(youth => youth.pawn.pawnId === Number(newValue));
                                dispatch(setSelectedYouth(selected));
                            }}
                            value={selectedYouth ? selectedYouth.pawn.pawnId : 'All'}
                        >
                            <option key={-1} value="all">
                                All
                            </option>
                            {youthList.map((youth, i) => {
                                return (
                                    <option
                                        value={youth.pawn.pawnId}
                                        key={i}
                                    >{`${youth.fields.firstName} ${youth.fields.lastName}`}</option>
                                );
                            })}
                        </select>
                        <button
                            type="button"
                            className={`btn btn-primary ${style.csv_button}`}
                            onClick={() => {
                                const filename = selectedYouth
                                    ? `${selectedYouth.fields.wrapId}-aggregate_data.csv`
                                    : 'aggregate_data.csv';
                                downloadCSV(aggregateScoreAverages, allAnswerData, filename);
                            }}
                        >
                            Download CSV
                        </button>
                    </div>
                </div>
                <div>
                    <div className={style.spacer} />

                    <div className={`${style.dividing_line} ${style.dividing_line_top}`} />

                    <div className={style.filters_container}>
                        <div className={style.filters_flex_row}>
                            <div>
                                <label className="form-label">Date Range: </label>{' '}
                                {(filters.startDate || filters.endDate) && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-xs"
                                        onClick={() => {
                                            dispatch(setStartDateFilter(''));
                                            dispatch(setEndDateFilter(''));
                                        }}
                                    >
                                        clear
                                    </button>
                                )}
                                <br />
                                <div className={style.date_range_container}>
                                    <div>
                                        <div>Start: </div>
                                        <DatePicker
                                            selected={filters.startDate}
                                            onChange={e => {
                                                dispatch(setStartDateFilter(e));
                                            }}
                                            placeholderText="mm/dd/yyyy"
                                        />
                                    </div>
                                    <div>
                                        <div>End: </div>
                                        <DatePicker
                                            selected={filters.endDate}
                                            onChange={e => {
                                                const newValue =
                                                    e === null ? '' : new Date(new Date(e).setHours(23, 59, 59, 999));
                                                dispatch(setEndDateFilter(newValue));
                                            }}
                                            placeholderText="mm/dd/yyyy"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="form-label">Plan Name: </label>
                                <select
                                    className="form-select"
                                    onChange={e => {
                                        dispatch(setPlanNameFilter(e.target.value));
                                    }}
                                    value={filters.planName}
                                >
                                    <option value="" />
                                    {Object.keys(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES).map((planName, i) => {
                                        return (
                                            <option key={i} value={planName.toLowerCase()}>
                                                {switchPlanNameIfNeeded(planName)}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className={style.main}>
                        <div className={style.response_rate_boxes}>
                            <ResponseRateBox label="Youth" />
                            <br />
                            <ResponseRateBox label="Caregiver" />
                            <br />
                            <AlertsResponseRateBox />
                        </div>
                        <AggregateAverages />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AggregateDataTab;
